import React, { useState, useEffect, useCallback } from "react";
import { Trash2, Pencil, Plus, Check, X, RefreshCw } from "lucide-react";
import Banner from "../../components/Banner";
import ConfirmModal from "../../components/ConfirmModal";

export default function AdminSiteSettings() {
  const [allowedCities, setAllowedCities] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [cutoffTime, setCutoffTime] = useState("");
  const [sameDayFee, setSameDayFee] = useState("");
  const [newCity, setNewCity] = useState("");
  const [editingCityIndex, setEditingCityIndex] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cityToDelete, setCityToDelete] = useState(null);

  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerType, setBannerType] = useState("success");

  const [currentPage, setCurrentPage] = useState(1);
  const citiesPerPage = 10;

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const [searchQuery, setSearchQuery] = useState("");

  const filteredCities = allowedCities.filter((city) =>
    city.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/settings`);
      if (!res.ok) throw new Error("Failed to fetch site settings");

      const data = await res.json();

      setAllowedCities(data.cities || []);
      setCutoffTime(data.cutoffTime || "15:00");
      setSameDayFee(data.sameDayFee ?? 50); // fallback to 50 if undefined or null
    } catch (error) {
      console.error("Fetch error:", error);
      alert("Failed to load site settings.");
    }
  }, [BASE_URL]);


  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);


  const handleSave = async () => {
    try {
      setIsSaving(true);

      const sortedCities = [...allowedCities].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      );

      const res = await fetch(`${BASE_URL}/api/settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          cities: sortedCities,
          cutoffTime,
          sameDayFee,
        }),
      });

      if (!res.ok) throw new Error("Failed to save settings");

      const result = await res.json();
      console.log("Save response:", result);

      setAllowedCities(sortedCities);
      setBannerMessage("Settings saved successfully!");
      setBannerType("success");
    } catch (err) {
      console.error(err);
      setBannerMessage("Failed to save settings");
      setBannerType("error");
    } finally {
      setIsSaving(false);
      setTimeout(() => setBannerMessage(""), 3000);
    }
  };

  const removeCity = (city) => {
    setAllowedCities((prev) => prev.filter((c) => c !== city));
  };

  const addOrUpdateCity = () => {
    const trimmed = newCity.trim();
    if (!trimmed) return;

    if (editingCityIndex !== null) {
      const updatedCities = [...allowedCities];
      updatedCities[editingCityIndex] = trimmed;
      setAllowedCities(updatedCities);
      setEditingCityIndex(null);
    } else {
      // Remove any existing entry (case-insensitive), then add new to top
      const withoutDuplicates = allowedCities.filter(
        city => city.toLowerCase() !== trimmed.toLowerCase()
      );
      setAllowedCities([trimmed, ...withoutDuplicates]);
    }

    setNewCity("");
  };

  // Pagination Logic
  const totalPages = Math.ceil(filteredCities.length / citiesPerPage);
  const currentCities = filteredCities.slice(
    (currentPage - 1) * citiesPerPage,
    currentPage * citiesPerPage
  );

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleRefresh = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/settings`);
      if (!res.ok) throw new Error("Failed to refresh settings");

      const data = await res.json();

      setAllowedCities(data.cities || []);
      setCutoffTime(data.cutoffTime || "15:00");
      setSameDayFee(data.sameDayFee ?? 50);
      setSearchQuery(""); // Reset search input

      setBannerMessage("City list refreshed");
      setBannerType("info");
      setTimeout(() => setBannerMessage(""), 3000);
    } catch (err) {
      console.error("Refresh error:", err);
      setBannerMessage("Failed to refresh list");
      setBannerType("error");
      setTimeout(() => setBannerMessage(""), 3000);
    }
  };

  return (
    <div className="p-4">
      <Banner type={bannerType} message={bannerMessage} />

      <h1 className="text-2xl font-bold mb-6">Site Settings</h1>

      {/* General Settings */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div>
          <label className="block font-medium mb-1">Same-day Cutoff Time</label>
          <input
            type="time"
            className="border p-2 rounded w-full"
            value={cutoffTime}
            onChange={(e) => setCutoffTime(e.target.value)}
          />
        </div>

        <div>
          <label className="block font-medium mb-1">Same-Day Delivery Fee ($)</label>
          <input
            type="number"
            min={0}
            step={0.01}
            className="border p-2 rounded w-full"
            value={sameDayFee}
            onChange={(e) => setSameDayFee(Number(e.target.value))}
          />
        </div>
      </div>

      {/* Delivery Cities */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <label className="font-bold text-lg">Allowed Delivery Cities</label>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Add/Edit City Input */}
          <div className="w-full flex-1">
            <label className="block mb-1 font-medium text-gray-700">
              {editingCityIndex !== null ? "Edit City" : "Add City"}
            </label>
            <div className="flex gap-2 w-full sm:w-auto">
              <input
                type="text"
                className="border p-2 rounded w-full"
                placeholder="Enter city name"
                value={newCity}
                onChange={(e) => setNewCity(e.target.value)}
              />
              <button
                className={`${
                  editingCityIndex !== null ? "bg-green-600 hover:bg-green-500" : "bg-gray-800 hover:bg-gray-700"
                } text-white px-4 py-2 rounded flex items-center gap-1`}
                onClick={addOrUpdateCity}
              >
                {editingCityIndex !== null ? <Check size={16} /> : <Plus size={16} />}
                {editingCityIndex !== null ? "Update" : "Add"}
              </button>
              {editingCityIndex !== null && (
                <button
                  className="flex items-center gap-1 bg-red-500 text-white px-3 py-2 rounded hover:bg-red-400"
                  onClick={() => {
                    setNewCity("");
                    setEditingCityIndex(null);
                  }}
                >
                  <X size={16} />
                  Cancel
                </button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="w-full flex-1">
            <label className="block mb-1 font-medium text-gray-700">Search Cities</label>
            <div className="flex gap-2 w-full sm:w-auto">
              <input
                type="text"
                className="border p-2 rounded w-full"
                placeholder="Type here to search cities"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

            </div>
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            className="flex items-center justify-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded hover:bg-blue-200 text-sm w-full sm:w-auto text-center"
            title="Refresh City List"
          >
            <RefreshCw size={16} />
            Refresh City List
          </button>

          {/* Save Button */}
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500 disabled:opacity-50 text-sm w-full sm:w-auto"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Settings"}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border rounded shadow-sm">
            <thead>
              <tr className="bg-gray-100 text-left text-sm font-medium text-gray-700">
                <th className="p-3">City</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentCities.length === 0 && (
                <tr>
                  <td colSpan="2" className="p-4 text-center text-sm text-gray-500">
                    No cities found.
                  </td>
                </tr>
              )}
              {currentCities.map((city, index) => (
                <tr key={index} className="border-t text-sm">
                  <td className="p-3">{city}</td>
                  <td className="p-3 text-right flex justify-end gap-3">
                    <button
                      className="text-gray-600 hover:text-gray-800 flex items-center gap-1"
                      onClick={() => {
                        setNewCity(city);
                        const fullIndex = allowedCities.findIndex((c) => c === city);
                        setEditingCityIndex(fullIndex);
                      }}
                      title="Edit"
                    >
                      <Pencil size={16} />
                      Edit
                    </button>

                    <button
                      className="text-red-500 hover:text-red-600 flex items-center gap-1"
                      onClick={() => {
                        setCityToDelete(city);
                        setConfirmOpen(true);
                      }}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-center gap-4 mt-4">
        <button
          onClick={() => goToPage(currentPage - 1)}
          className="px-3 py-2 rounded bg-blue-600 text-white text-xs hover:bg-blue-500 disabled:bg-gray-300 disabled:text-gray-600"
          disabled={currentPage === 1}
        >
          Prev
        </button>

        <span className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </span>

        <button
          onClick={() => goToPage(currentPage + 1)}
          className="px-3 py-2 rounded bg-blue-600 text-white text-xs hover:bg-blue-500 disabled:bg-gray-300 disabled:text-gray-600"
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      <ConfirmModal
        isOpen={confirmOpen}
        title="Delete City"
        message={`Are you sure you want to delete "${cityToDelete}"?`}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => {
          if (cityToDelete) {
            removeCity(cityToDelete);
            setConfirmOpen(false);
          }
        }}
        confirmLabel="Yes, Delete"
        cancelLabel="Cancel"
      />

    </div>
    
  );
}
